import React, { useEffect, useState } from 'react';
import BA2 from './BA2.jpg';
import BA3 from './BA3.jpeg';
import BA4 from './BA4.png';
import BA5 from './BA5.jpg';
import BA6 from './BA6.jpg';
import BA7 from './BA Chemical Peel.jpeg';

import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const BeforeAfterCard = ({ image, service, description, category }) => (
  <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-0 bg-white/95 backdrop-blur-sm">
    <div className="relative overflow-hidden group">
      <div className="w-full">
        <img 
          src={image} 
          alt={`${service} results`} 
          className="w-full h-auto max-h-[400px] object-contain bg-gray-50"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      <Badge className="absolute top-4 right-4 bg-blue-600 text-white text-xs sm:text-sm">{category}</Badge>
    </div>
    <CardContent className="p-4 sm:p-6 space-y-2 sm:space-y-3">
      <h3 className="text-xl sm:text-2xl font-bold font-serif text-gray-900">
        {service}
      </h3>
      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{description}</p>
    </CardContent>
  </Card>
);

const BeforeAfter = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const beforeAfterData = [
    {
      image: BA2,
      service: "Body Sculpting",
      category: "Body Contouring",
      description: "This client experienced significant body contouring results through a combination of Wood Therapy, Cavitation with RF, vacuum therapy, and electromagnetic sculpting techniques."
    },
    {
      image: BA3,
      service: "Fibroblast Eye Treatment",
      category: "Skin Tightening",
      description: "Eye area improvement achieved with Fibroblast plasma technology, helping to tighten skin and reduce the appearance of fine lines without surgery."
    },
    {
      image: BA4,
      service: "Advanced Body Sculpting",
      category: "Body Contouring",
      description: "Body sculpting results from combining Wood therapy with RF technology and electromagnetic therapy to help reshape and contour targeted areas."
    },
    {
      image: BA5,
      service: "Cryo T-Shock Therapy",
      category: "Fat Reduction",
      description: "Visible changes in body composition using Cryo T-Shock therapy combined with electromagnetic sculpting to target stubborn areas."
    },
    {
      image: BA6,
      service: "Microneedling Transformation",
      category: "Skin Rejuvenation",
      description: "Skin texture and tone improvements following a series of Microneedling treatments designed to stimulate natural collagen production."
    },
    {
      image: BA7,
      service: "Chemical Peel Results",
      category: "Skin Rejuvenation",
      description: "Chemical Peel results showing clearer skin texture and more even tone after professional treatment tailored to the client's specific needs."
    }
  ];

  const categories = ['All', 'Body Contouring', 'Skin Rejuvenation', 'Fat Reduction', 'Skin Tightening'];
  const filteredData = selectedCategory === 'All' 
    ? beforeAfterData 
    : beforeAfterData.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700">
        {/* Background pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)',
          }}></div>
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-24">
          <div className="flex flex-col items-center justify-center text-center space-y-6">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white font-serif">
              See Our Results
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl">
              Client results from our treatments
            </p>
          </div>
        </div>
        
        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-16" viewBox="0 0 1440 100" fill="#f0f9ff">
            <path d="M0,50 C360,100 720,0 1440,50 L1440,100 L0,100 Z"></path>
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 -mt-8 relative z-10">


        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? "default" : "outline"}
              className={selectedCategory === category 
                ? "bg-gradient-to-r from-blue-600 to-cyan-600 text-white" 
                : "hover:bg-blue-50"
              }
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Intro Text */}
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <p className="text-lg text-gray-600">
            These are actual results from clients who have undergone treatments at our medical spa.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredData.map((item, index) => (
            <BeforeAfterCard key={index} {...item} />
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center py-12 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-3xl">
          <div className="max-w-2xl mx-auto space-y-6 px-6">
            <h2 className="text-3xl font-bold font-serif text-gray-900">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-gray-600">
              Schedule a consultation to discuss your goals and explore treatment options.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/booking" 
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg transition-all duration-300"
              >
                Book Appointment
              </a>
              <a 
                href="/services" 
                className="inline-block bg-white border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-blue-50 transition-all duration-300"
              >
                View Services
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeforeAfter;